import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Divider,
  CircularProgress,
  Chip
} from '@mui/material';
import { FileText, Sparkles, HelpCircle, Check } from 'lucide-react';
import FormattingValidation from './FormattingValidation';

import { estReprenable, retenir, relire, oublier } from '../utils/validationEnAttente';

const PANEL = {
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 2,
  p: 1.5,
  mb: 1
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('portalToken')}`
});

/**
 * Dialogue unique des actions sur la documentation d'un devis :
 * génération IA, génération des sections manquantes, et correction de mise en
 * forme — déterministe ou confiée à l'agent.
 *
 * En mode validation, l'agent prépare ses corrections sans rien écrire : le
 * dialogue passe en phase « validation », affiche le diff de chaque document et
 * n'écrit que ce que l'utilisateur accepte. L'agent peut aussi rendre la main
 * pour poser une question — phase « question » — mais ce n'est plus ce qui
 * définit le mode.
 */
function DocsActionDialog({ open, onClose, submissionId, sections, onDone }) {
  const sectionsWithDoc = sections.filter(
    (s) => s.attachments?.some((a) => a.name?.endsWith('.md'))
  ).length;
  const sectionsWithoutDoc = sections.length - sectionsWithDoc;

  const [phase, setPhase] = useState('setup');
  const [action, setAction] = useState('missing');
  const [engine, setEngine] = useState('normal');
  const [execMode, setExecMode] = useState('auto');
  const [level, setLevel] = useState(2);
  const [levels, setLevels] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState('');
  const [result, setResult] = useState(null);
  const [propositions, setPropositions] = useState(null);
  const [ignores, setIgnores] = useState([]);
  const [resumeValidation, setResumeValidation] = useState('');
  const [error, setError] = useState('');
  const [diagnostic, setDiagnostic] = useState(null);
  const [appliedAvantEchec, setAppliedAvantEchec] = useState(null);
  const [ecriture, setEcriture] = useState(false);
  const [reprise, setReprise] = useState(null);
  const [secondes, setSecondes] = useState(0);
  const [progres, setProgres] = useState(null);
  const suiviActif = useRef(false);
  const etaitOuvert = useRef(false);

  const currentLevel = levels.find((l) => l.level === level);

  // Les niveaux viennent du backend : les options affichées correspondent ainsi
  // toujours à des consignes réellement envoyées au modèle.
  useEffect(() => {
    if (!open || levels.length > 0) return;
    (async () => {
      try {
        const res = await fetch('/api/ai/doc/format/options', { headers: authHeaders() });
        if (res.ok) setLevels((await res.json()).levels || []);
      } catch (err) {
        console.error('Formatting options error:', err);
      }
    })();
  }, [open, levels.length]);

  // Changer de niveau resélectionne tout ce que ce niveau autorise.
  useEffect(() => {
    if (currentLevel) setChosen(currentLevel.options.map((o) => o.key));
  }, [currentLevel]);

  useEffect(() => {
    if (!open) {
      suiviActif.current = false;
      etaitOuvert.current = false;
      return;
    }

    // Uniquement a l'ouverture. Sans ce garde, l'effet se redeclenche quand le
    // parent rafraichit apres un traitement — le nombre de documents change —
    // et efface le resultat que l'utilisateur est en train de lire.
    if (etaitOuvert.current) return;
    etaitOuvert.current = true;

    setProgres(null);
    setPhase('setup');
    setResult(null);
    setPropositions(null);
    setIgnores([]);
    setResumeValidation('');
    setError('');
    setDiagnostic(null);
    setAppliedAvantEchec(null);
    setEcriture(false);
    setQuestion(null);
    setAnswer('');
    setConversationId(null);
    setReprise(null);

    // Remise a zero des reglages : rouvrir la fenetre doit repartir des
    // valeurs par defaut, pas de celles du traitement precedent.
    setEngine('normal');
    setExecMode('auto');
    setLevel(2);

    if (sectionsWithDoc === 0) setAction('all');
    else if (sectionsWithoutDoc > 0) setAction('missing');
    else setAction('reformat');

    // Une validation laissee en attente : ses propositions sont en base, mais
    // rien n'allait les y chercher. On relit l'identifiant retenu et on
    // interroge le statut existant — son repli lit la base quand le registre
    // en memoire a ete vide. Un identifiant perime est simplement oublie.
    const retenu = relire(submissionId);
    if (retenu) {
      (async () => {
        try {
          const etat = await lireStatut(retenu);
          if (estReprenable(etat)) setReprise({ conversationId: retenu, etat });
          else oublier(submissionId);
        } catch (err) {
          // Surtout PAS `oublier` ici : `lireStatut` leve sur un 401, un 502
          // pendant un deploiement, ou une seconde hors ligne. Effacer le
          // pointeur sur un alea reseau detruirait definitivement des
          // propositions qui ont coute un appel au modele — soit exactement
          // ce que cette fonctionnalite existe pour eviter.
        }
      })();
    }
  }, [open, sectionsWithDoc, sectionsWithoutDoc, submissionId]);

  // Sonde : un traitement coupé par un proxy se reconnaît à sa durée. Afficher
  // le chronomètre rend la cause lisible sans aller chercher dans les logs.
  useEffect(() => {
    if (phase !== 'running') return undefined;
    setSecondes(0);
    const t = setInterval(() => setSecondes((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [phase]);

  const toggleOption = (key) =>
    setChosen((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  // Un proxy qui coupe renvoie une page HTML, pas du JSON : appeler res.json()
  // directement lève une erreur d'analyse qui masque le vrai statut, et
  // l'utilisateur lit « unexpected character » au lieu de « 504 ».
  const post = async (url, body) => {
    const t0 = Date.now();
    let res;
    try {
      res = await fetch(url, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
    } catch (reseau) {
      const err = new Error(`La connexion a été coupée après ${Math.round((Date.now() - t0) / 1000)} s : ${reseau.message}`);
      err.diagnostic = { duree: Math.round((Date.now() - t0) / 1000), statut: 'connexion perdue', url };
      throw err;
    }

    const duree = Math.round((Date.now() - t0) / 1000);
    const brut = await res.text();
    const detail = {
      duree,
      statut: res.status,
      typeContenu: res.headers.get('content-type') || 'absent',
      url,
      debutCorps: brut.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160)
    };
    console.log('[mise en forme] réponse', detail);

    let data = null;
    try {
      data = brut ? JSON.parse(brut) : null;
    } catch (err) {
      const apercu = brut.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120);
      const echec = res.status === 504 || res.status === 502
        ? new Error(`Le serveur a mis trop de temps à répondre (${res.status}) après ${duree} s.`)
        : new Error(`Réponse inattendue du serveur (${res.status}) après ${duree} s : ${apercu || 'corps vide'}`);
      echec.diagnostic = detail;
      throw echec;
    }

    if (!res.ok) {
      const err = new Error(data?.error || `Échec de la requête (${res.status}) après ${duree} s`);
      err.diagnostic = detail;
      // /apply met deliberement « applied » dans le corps du 500 (voir
      // docFormatterController.js) : ce qui est deja ecrit avant l'echec ne
      // doit pas se perdre en cours de route jusqu'a l'appelant.
      err.applied = data?.applied;
      throw err;
    }
    return { ...data, _duree: duree };
  };

  const lireStatut = async (conversationId) => {
    const res = await fetch(`/api/ai/doc/format/${conversationId}/status`, { headers: authHeaders() });
    const brut = await res.text();
    let data = null;
    try { data = brut ? JSON.parse(brut) : null; } catch (err) { data = null; }
    if (!res.ok || !data) throw new Error(data?.error || `Suivi indisponible (${res.status})`);
    return data;
  };

  /**
   * L'agent tourne en arrière-plan : on interroge son avancement.
   *
   * Aucune requête ne dure donc plus de quelques centièmes de seconde, ce qui
   * met le traitement hors d'atteinte du plafond de 90 s de Cloudflare — la
   * cause du « 504 » observé le 2026-09-18.
   */
  /**
   * Entree dans l'ecran de validation, partagee par le suivi d'un traitement
   * qui vient de finir et par la reprise d'une validation laissee en attente.
   * Les deux chemins doivent afficher exactement la meme chose.
   *
   * `error` et `summary` accompagnent le statut au lieu de le remplacer : le
   * backend produit l'avertissement « aucun document traite » dans `error` a
   * cote de `awaiting_validation`, et met dans `summary` le « limite d'etapes
   * atteinte » d'une execution coupee en route. Les ignorer annulerait
   * silencieusement ce travail.
   */
  const entrerEnValidation = (id, etat) => {
    const propositionsRecues = etat.propositions || [];
    setConversationId(id);
    setPropositions(propositionsRecues);
    setIgnores((etat.done || []).filter(
      (f) => !propositionsRecues.some((p) => p.documentId === f.documentId)
    ));
    setError(etat.error || '');
    setResumeValidation(etat.summary || '');
    setProgres({ done: etat.done || [], current: null, total: etat.documentCount });
    setReprise(null);
    // Rien n'est encore ecrit : si la fenetre se ferme maintenant, c'est cet
    // identifiant qui permettra d'y revenir.
    retenir(submissionId, id);
    setPhase('validation');
  };

  const suivre = async (conversationId) => {
    suiviActif.current = true;
    while (suiviActif.current) {
      await new Promise((r) => setTimeout(r, 2000));
      if (!suiviActif.current) return;

      let etat;
      try {
        etat = await lireStatut(conversationId);
      } catch (err) {
        suiviActif.current = false;
        setError(err.message);
        setPhase('setup');
        return;
      }

      setProgres({ done: etat.done || [], current: etat.current, total: etat.documentCount });

      if (etat.status === 'running') continue;

      suiviActif.current = false;

      if (etat.status === 'awaiting_answer') {
        setConversationId(conversationId);
        setQuestion({ text: etat.question, context: etat.context });
        setPhase('question');
        setAnswer('');
      } else if (etat.status === 'awaiting_validation') {
        // L'agent n'a rien écrit : il a préparé des corrections, on les valide.
        entrerEnValidation(conversationId, etat);
      } else if (etat.status === 'error' || etat.status === 'interrupted') {
        setError(etat.error || 'Le traitement a échoué.');
        setPhase('setup');
        if (onDone) onDone();
      } else {
        setResult(etat);
        setPhase('done');
        if (onDone) onDone();
      }
      return;
    }
  };

  const launch = async () => {
    setPhase('running');
    setError('');
    setDiagnostic(null);
    setAppliedAvantEchec(null);
    setPropositions(null);
    setIgnores([]);
    setResumeValidation('');
    setResult(null);
    try {
      if (action === 'reformat' && engine === 'ai') {
        const demarre = await post('/api/ai/doc/format', {
          submissionId, mode: execMode, level, options: chosen
        });
        setConversationId(demarre.conversationId);
        retenir(submissionId, demarre.conversationId);
        setProgres({ done: [], current: null, total: demarre.documentCount });
        await suivre(demarre.conversationId);
        return;
      }

      const data = action === 'reformat'
        ? await post('/api/ai/doc/reformat', { submissionId })
        : await post('/api/ai/doc/generate-all', { submissionId, mode: action });

      setResult(data);
      // Tranche : cette validation n'attend plus, ne plus la proposer.
      oublier(submissionId);
      setPhase('done');
      if (onDone) onDone();
    } catch (err) {
      setError(err.message);
      setDiagnostic(err.diagnostic || null);
      setPhase('setup');
    }
  };

  const sendAnswer = async () => {
    if (!answer.trim()) return;
    setPhase('running');
    setError('');
    try {
      await post(`/api/ai/doc/format/${conversationId}/answer`, { answer });
      await suivre(conversationId);
    } catch (err) {
      setError(err.message);
      setDiagnostic(err.diagnostic || null);
      setPhase('question');
    }
  };

  /**
   * Ecrit les propositions acceptees.
   *
   * Ne change PAS de phase pendant l'ecriture : rester en « validation » est
   * ce qui garde FormattingValidation monte, donc ses cases cochees intactes
   * si la requete echoue. Repasser par « running » le demonterait, et son
   * remontage aurait reinitialise l'acceptation de chaque proposition a
   * « tout coche » via l'initialiseur paresseux de `acceptes` — un second
   * clic aurait alors pu ecrire des documents que l'utilisateur venait de
   * decocher. `ecriture` porte donc seul l'etat "en cours" ; `busy` (qui
   * pilote aussi le bouton Annuler) en tient compte.
   */
  const appliquer = async (documentIds) => {
    setError('');
    setDiagnostic(null);
    setAppliedAvantEchec(null);
    setEcriture(true);
    try {
      const data = await post(`/api/ai/doc/format/${conversationId}/apply`, { documentIds });
      setResult({
        summary: data.summary,
        done: (data.applied || []).map((a) => ({ section: a.section, resume: a.summary })),
        rejected: data.rejected || []
      });
      setPhase('done');
      if (onDone) onDone();
    } catch (err) {
      setError(err.message);
      setDiagnostic(err.diagnostic || null);
      // Ce qui a deja ete ecrit avant l'echec (voir post()) reste affiche :
      // sans base pour invalider une proposition, un second clic sur les
      // memes documents la rejoue de toute facon sans degat (ecriture
      // idempotente cote backend), mais l'utilisateur doit savoir que ce
      // sous-ensemble est deja fait plutot que de le supposer perdu.
      setAppliedAvantEchec(err.applied?.length > 0 ? err.applied : null);
    } finally {
      setEcriture(false);
    }
  };

  const busy = phase === 'running' || ecriture;

  // Un document ni proposé ni laissé tel quel n'a jamais été examiné : il
  // n'apparaît nulle part ailleurs à l'écran — ni dans les diffs, ni dans
  // « Laissés tels quels par l'agent ». Une exécution coupée au plafond
  // d'étapes se présentait donc comme complète.
  const nonExamines = Math.max(
    0,
    (progres?.total || 0) - ((propositions?.length || 0) + ignores.length)
  );

  const actionChoices = [
    {
      value: 'all',
      label: 'Régénérer tout (IA)',
      hint: sectionsWithDoc > 0
        ? `Écrase les ${sectionsWithDoc} documentations existantes.`
        : `Génère la documentation des ${sections.length} sections.`,
      disabled: false
    },
    {
      value: 'missing',
      label: 'Seulement les sections sans doc (IA)',
      hint: sectionsWithoutDoc > 0
        ? `Génère ${sectionsWithoutDoc} documentation(s). Ne touche pas aux ${sectionsWithDoc} autres.`
        : 'Aucune section sans documentation.',
      disabled: sectionsWithoutDoc === 0
    },
    {
      value: 'reformat',
      label: `Corriger la mise en forme des ${sectionsWithDoc} docs`,
      hint: 'Normalise le Markdown pour le rendu PDF.',
      disabled: sectionsWithDoc === 0
    }
  ];

  return (
    <Dialog
      open={open}
      // Un clic sur le fond ne ferme pas une validation en attente : les
      // propositions ont coûté un appel LLM et rien, dans l'interface, ne
      // permet de revenir sur une validation abandonnée. Le bouton « Annuler »
      // reste le chemin explicite d'abandon.
      onClose={() => !busy && phase !== 'validation' && onClose()}
      maxWidth={phase === 'validation' ? 'md' : 'sm'}
      fullWidth
      PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
        <FileText size={18} />
        Documentation du devis
      </DialogTitle>

      <DialogContent>
        {error && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error" variant="body2">{error}</Typography>
            {appliedAvantEchec && (
              <Box sx={{ ...PANEL, mt: 1, bgcolor: 'rgba(0,212,170,0.06)', borderColor: 'rgba(0,212,170,0.3)' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Déjà écrit avant l'échec ({appliedAvantEchec.length}), inutile de le réappliquer :
                </Typography>
                {appliedAvantEchec.map((a) => (
                  <Typography key={a.documentId} variant="caption" sx={{ display: 'block' }}>
                    • {a.section}{a.summary ? ` — ${a.summary}` : ''}
                  </Typography>
                ))}
              </Box>
            )}
            {diagnostic && (
              <Box sx={{ ...PANEL, mt: 1, bgcolor: 'rgba(239,68,68,0.06)', borderColor: 'rgba(239,68,68,0.3)' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Détail à me transmettre
                </Typography>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                  {`durée      : ${diagnostic.duree} s
statut     : ${diagnostic.statut}
type       : ${diagnostic.typeContenu || '-'}
appel      : ${diagnostic.url}
début corps: ${diagnostic.debutCorps || '-'}`}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {phase === 'running' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
            <CircularProgress size={24} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {action === 'reformat' && engine === 'ai'
                  ? 'L’agent relit et corrige les documents…'
                  : 'Traitement en cours…'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', display: 'block' }}>
                {Math.floor(secondes / 60)} min {String(secondes % 60).padStart(2, '0')} s
                {progres?.total ? ` · ${progres.done.length}/${progres.total} document(s)` : ''}
              </Typography>
              {progres?.current && (
                <Typography variant="caption" sx={{ color: 'primary.main', display: 'block' }}>
                  en cours : {progres.current}
                </Typography>
              )}
              {progres?.done?.length > 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  terminé : {progres.done.map((d) => d.section).join(' · ')}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {phase === 'question' && question && (
          <Box sx={{ py: 1 }}>
            <Box sx={{ ...PANEL, bgcolor: 'rgba(124,58,237,0.08)', borderColor: 'rgba(124,58,237,0.3)' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <HelpCircle size={18} color="#a78bfa" style={{ marginTop: 2, flexShrink: 0 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{question.text}</Typography>
                  {question.context && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                      {question.context}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            <TextField
              fullWidth
              size="small"
              autoFocus
              multiline
              minRows={2}
              placeholder="Votre réponse…"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              sx={{ mt: 1 }}
            />
          </Box>
        )}

        {phase === 'validation' && propositions && (
          <>
            {/* L'ambre est réservée au cas RÉELLEMENT incomplet. Sur le chemin
                nominal, `summary` porte toujours la phrase de conclusion du
                modèle : en faire un bandeau d'avertissement le rendait
                permanent, et un signal permanent n'est plus un signal. */}
            {resumeValidation && nonExamines === 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
                {resumeValidation}
              </Typography>
            )}
            {nonExamines > 0 && (
              <Box sx={{ ...PANEL, mt: 1, bgcolor: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.3)' }}>
                {resumeValidation && (
                  <Typography variant="caption" sx={{ display: 'block' }}>{resumeValidation}</Typography>
                )}
                <Typography variant="caption" sx={{ display: 'block' }}>
                  {nonExamines} document(s) sur {progres.total} n'ont pas été examinés par l'agent :
                  relancer le traitement après cette validation pour les traiter.
                </Typography>
              </Box>
            )}
            <FormattingValidation
              propositions={propositions}
              ignores={ignores}
              busy={busy}
              onApply={appliquer}
            />
          </>
        )}

        {phase === 'done' && result && (
          <Box sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
              <Check size={18} color="#00d4aa" style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography variant="body2">{result.summary || result.message}</Typography>
            </Box>

            {/* On affiche « done », qui porte le nom de section ; « applied » n'a
                que l'identifiant du document et rendait les lignes indiscernables. */}
            {result.done?.length > 0 && (
              <Box sx={{ ...PANEL, bgcolor: 'rgba(255,255,255,0.02)' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                  {result.done.length} document(s) traité(s)
                </Typography>
                {result.done.map((d, index) => (
                  <Box
                    key={`${d.section}-${index}`}
                    sx={{
                      mb: index === result.done.length - 1 ? 0 : 1,
                      pb: index === result.done.length - 1 ? 0 : 1,
                      borderBottom: index === result.done.length - 1
                        ? 'none'
                        : '1px solid rgba(255,255,255,0.05)'
                    }}
                  >
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                      {d.section}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      {d.resume || 'mis en forme'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {result.rejected?.length > 0 && (
              <Box sx={{ ...PANEL, bgcolor: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.3)' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  {result.rejected.length} non appliqué(s)
                </Typography>
                {result.rejected.map((r) => (
                  <Typography key={r.documentId} variant="caption" sx={{ display: 'block' }}>
                    • {r.section || 'Document inconnu'} : {r.reason}
                  </Typography>
                ))}
              </Box>
            )}

            {result.results?.some((r) => r.warnings?.length > 0) && (
              <Box sx={{ ...PANEL, bgcolor: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.3)' }}>
                {result.results.filter((r) => r.warnings?.length > 0).map((r) => (
                  <Typography key={r.sectionId} variant="caption" sx={{ display: 'block' }}>
                    • {r.sectionName} : {r.warnings.join(' ; ')}
                  </Typography>
                ))}
              </Box>
            )}

            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5 }}>
              Chaque modification est enregistrée dans l'historique du document : elle reste annulable.
            </Typography>
          </Box>
        )}

        {phase === 'setup' && (
          <>
            {/* Des propositions preparees et jamais tranchees : elles sont en
                base, intactes. Sans ce rappel, il fallait relancer un
                traitement — et repayer un appel au modele — pour les revoir. */}
            {reprise && (
              <Box sx={{ ...PANEL, mt: 1, bgcolor: 'rgba(0,212,170,0.06)', borderColor: 'rgba(0,212,170,0.3)' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Une validation est en attente
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                  {reprise.etat.propositions.length} correction(s) préparée(s) précédemment,
                  toujours pas écrites.
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => entrerEnValidation(reprise.conversationId, reprise.etat)}
                >
                  Reprendre la validation
                </Button>
              </Box>
            )}

            {sectionsWithDoc > 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, mb: 2 }}>
                {sectionsWithDoc} des {sections.length} sections ont déjà une documentation.
              </Typography>
            )}

            <RadioGroup value={action} onChange={(e) => setAction(e.target.value)}>
              {actionChoices.map((choice) => (
                <Box
                  key={choice.value}
                  sx={{
                    ...PANEL,
                    opacity: choice.disabled ? 0.45 : 1,
                    bgcolor: action === choice.value ? 'rgba(0,212,170,0.06)' : 'rgba(255,255,255,0.02)'
                  }}
                >
                  <FormControlLabel
                    value={choice.value}
                    disabled={choice.disabled}
                    control={<Radio size="small" />}
                    sx={{ alignItems: 'flex-start', m: 0 }}
                    label={
                      <Box sx={{ pt: 0.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{choice.label}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{choice.hint}</Typography>
                      </Box>
                    }
                  />

                  {choice.value === 'reformat' && action === 'reformat' && (
                    <Box sx={{ mt: 1.5, pl: 3.5 }}>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={engine}
                        onChange={(e, v) => v && setEngine(v)}
                        sx={{ mb: 1 }}
                      >
                        <ToggleButton value="normal" sx={{ textTransform: 'none', px: 2 }}>Normal</ToggleButton>
                        <ToggleButton value="ai" sx={{ textTransform: 'none', px: 2 }}>
                          <Sparkles size={14} style={{ marginRight: 6 }} /> IA
                        </ToggleButton>
                      </ToggleButtonGroup>

                      {engine === 'normal' ? (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          Déterministe, instantané, gratuit. Titres, listes, espacement, HTML.
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                            L'agent relit chaque document et corrige selon le niveau choisi.
                          </Typography>

                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                            Exécution
                          </Typography>
                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={execMode}
                            onChange={(e, v) => v && setExecMode(v)}
                            sx={{ mb: 0.5 }}
                          >
                            <ToggleButton value="auto" sx={{ textTransform: 'none', px: 2 }}>Auto</ToggleButton>
                            <ToggleButton value="validation" sx={{ textTransform: 'none', px: 2 }}>Validation</ToggleButton>
                          </ToggleButtonGroup>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                            {execMode === 'auto'
                              ? 'L’agent corrige et écrit lui-même, puis présente le résultat.'
                              : 'L’agent prépare ses corrections sans rien écrire ; vous voyez le diff de chaque document et acceptez ou refusez.'}
                          </Typography>

                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                            Niveau
                          </Typography>
                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={level}
                            onChange={(e, v) => v && setLevel(v)}
                            sx={{ mb: 0.5 }}
                          >
                            {[1, 2, 3].map((n) => (
                              <ToggleButton key={n} value={n} sx={{ textTransform: 'none', px: 2 }}>{n}</ToggleButton>
                            ))}
                          </ToggleButtonGroup>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                            {currentLevel ? `${currentLevel.name} — ${currentLevel.summary}` : 'Chargement…'}
                          </Typography>

                          {currentLevel && (
                            <>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                Options
                              </Typography>
                              {currentLevel.options.map((option) => (
                                <Box key={option.key} sx={{ display: 'flex', alignItems: 'center' }}>
                                  <FormControlLabel
                                    sx={{ m: 0 }}
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={chosen.includes(option.key)}
                                        onChange={() => toggleOption(option.key)}
                                      />
                                    }
                                    label={<Typography variant="caption">{option.label}</Typography>}
                                  />
                                  {option.level < level && (
                                    <Chip
                                      label={`niv. ${option.level}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }}
                                    />
                                  )}
                                </Box>
                              ))}
                            </>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              ))}
            </RadioGroup>
          </>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={() => { if (phase === 'validation') oublier(submissionId); onClose(); }}
          color="inherit"
          disabled={busy}
        >
          {phase === 'done' ? 'Fermer' : 'Annuler'}
        </Button>

        {phase === 'setup' && (
          <Button
            variant="contained"
            onClick={launch}
            disabled={busy || (action === 'reformat' && engine === 'ai' && chosen.length === 0)}
          >
            Lancer
          </Button>
        )}

        {phase === 'question' && (
          <Button variant="contained" onClick={sendAnswer} disabled={!answer.trim()}>
            Répondre et continuer
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default DocsActionDialog;
